import React, { useState } from 'react';
import { Lock, Eye, EyeOff, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import logo from './imgs/Logo_Fcn.png';
import ErrorAlert from './components/ErrorAlert';
import { readJson, responseError, describeError } from './utils/http';

const ChangePassword = ({ username, onPasswordChanged, onCancel }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [error, setError] = useState('');
  const [errorKind, setErrorKind] = useState('user');
  const [isLoading, setIsLoading] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
  });

  const API_BASE_URL = process.env.REACT_APP_API_URL;

  const validatePassword = (password) => {
    const validation = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };
    setPasswordValidation(validation);
    return Object.values(validation).every(Boolean);
  };

  const handleNewPasswordChange = (e) => {
    const password = e.target.value;
    setNewPassword(password);
    validatePassword(password);
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setErrorKind('user');

    if (!currentPassword) {
      setError('Por favor ingrese su contraseña actual');
      setIsLoading(false);
      return;
    }

    if (!newPassword) {
      setError('Por favor ingrese su nueva contraseña');
      setIsLoading(false);
      return;
    }

    if (!validatePassword(newPassword)) {
      setError('La nueva contraseña no cumple con los requisitos de seguridad');
      setIsLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      setIsLoading(false);
      return;
    }

    if (currentPassword === newPassword) {
      setError('La nueva contraseña debe ser diferente a la actual');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          Usuario: username,
          Contrasenia: currentPassword,
          NuevaContrasenia: newPassword,
          action: 'change-password',
        }),
      });

      const data = await readJson(response);

      if (!response.ok) {
        throw responseError(response, data, 'Error al cambiar la contraseña');
      }

      onPasswordChanged();
    } catch (err) {
      console.error('Error al cambiar contraseña:', err);
      const { kind, message } = describeError(err, 'Error al cambiar la contraseña');
      setErrorKind(kind);
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const ValidationItem = ({ isValid, text }) => (
    <div className={`flex items-center text-sm ${isValid ? 'text-green-600' : 'text-red-600'}`}>
      {isValid ? <CheckCircle className="h-4 w-4 mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
      {text}
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <style>{`.relative .absolute:nth-child(n+2) { display: none; }`}</style>
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg overflow-hidden">
        <div style={{ backgroundColor: '#A1887F' }} className="py-6 px-6 flex justify-center">
          <div className="text-center">
            <img
              src={logo}
              alt="Logo Fondo Cafetero"
              className="h-20 mx-auto mb-2"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'https://via.placeholder.com/150?text=FCN';
              }}
            />
            <h2 className="text-2xl font-bold text-white">Cambio de Contraseña</h2>
            <p className="text-white text-sm mt-2">Debe cambiar su contraseña antes de continuar</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="py-8 px-8 space-y-6">
          {error && <ErrorAlert message={error} kind={errorKind} onClose={() => setError('')} />}

          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-blue-400" />
              </div>
              <div className="ml-3">
                <p className="text-blue-700 text-sm">
                  <strong>Usuario:</strong> {username}
                </p>
                <p className="text-blue-700 text-sm mt-1">
                  Por seguridad, debe establecer una nueva contraseña para su cuenta.
                </p>
              </div>
            </div>
          </div>

          {/* Contraseña Actual */}
          <div>
            <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
              Contraseña Actual
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="currentPassword"
                type={showPasswords.current ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="pl-10 pr-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-brown-500 focus:border-brown-500"
                placeholder="Ingrese su contraseña actual"
                required
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('current')}
                  className="focus:outline-none"
                >
                  {showPasswords.current ? <EyeOff className="h-5 w-5 text-gray-400" /> : <Eye className="h-5 w-5 text-gray-400" />}
                </button>
              </div>
            </div>
          </div>

          {/* Nueva Contraseña */}
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
              Nueva Contraseña
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="newPassword"
                type={showPasswords.new ? 'text' : 'password'}
                value={newPassword}
                onChange={handleNewPasswordChange}
                className="pl-10 pr-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-brown-500 focus:border-brown-500"
                placeholder="Ingrese su nueva contraseña"
                required
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('new')}
                  className="focus:outline-none"
                >
                  {showPasswords.new ? <EyeOff className="h-5 w-5 text-gray-400" /> : <Eye className="h-5 w-5 text-gray-400" />}
                </button>
              </div>
            </div>
          </div>

          {/* Validación de Contraseña */}
          {newPassword && (
            <div className="bg-gray-50 p-4 rounded-md">
              <p className="text-sm font-medium text-gray-700 mb-2">Requisitos de la contraseña:</p>
              <div className="space-y-1">
                <ValidationItem isValid={passwordValidation.length} text="Mínimo 8 caracteres" />
                <ValidationItem isValid={passwordValidation.uppercase} text="Al menos una mayúscula" />
                <ValidationItem isValid={passwordValidation.lowercase} text="Al menos una minúscula" />
                <ValidationItem isValid={passwordValidation.number} text="Al menos un número" />
                <ValidationItem isValid={passwordValidation.special} text="Al menos un carácter especial" />
              </div>
            </div>
          )}

          {/* Confirmar Contraseña */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
              Confirmar Nueva Contraseña
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="confirmPassword"
                type={showPasswords.confirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="pl-10 pr-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-brown-500 focus:border-brown-500"
                placeholder="Confirme su nueva contraseña"
                required
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('confirm')}
                  className="focus:outline-none"
                >
                  {showPasswords.confirm ? <EyeOff className="h-5 w-5 text-gray-400" /> : <Eye className="h-5 w-5 text-gray-400" />}
                </button>
              </div>
            </div>
          </div>

          {/* Botones */}
          <div className="flex space-x-4">
            <button
              type="submit"
              style={{ backgroundColor: '#8D6E63' }}
              className="flex-1 py-2 px-4 border border-transparent rounded-md text-white font-medium hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brown-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading || !Object.values(passwordValidation).every(Boolean)}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Cambiando...
                </div>
              ) : (
                'Cambiar Contraseña'
              )}
            </button>

            <button
              type="button"
              onClick={onCancel}
              className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-gray-700 font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition"
              disabled={isLoading}
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;